// WoQuickView (R112 — Jesse #5: "when I create a WO and then click on that
// green link, a pop-up with the WO"): ONE read-mostly work-order viewer shared
// by Supplies & Expenses, the Calendar popover and the Maintenance day plan.
// Mounted on document.body (the QuickLogIssue pattern) so a host page's draw()
// can never destroy it; an anchored popover when the caller hands an anchor
// on a wide screen, a centered modal / bottom sheet otherwise. Role="dialog",
// Esc + backdrop close, focus lands on the close button, Tab wraps inside.
//
// DATA: GET /api/assetops/work-orders/:id (the accounting partner gets its slim
// projection — no photos/items/log, and the viewer degrades to that). While the
// detail route answers, opts.wo (the host's own copy) renders instantly; if the
// detail read fails the viewer falls back to the list route (by listing when
// known) and finds the id there.
//
// R115 maint: GET /work-orders/:id now strips cost / payer / billed / skill for
// every role outside admin / director / accounting (money_hidden:true) — the
// Cost and Billed rows are left out then instead of reading "nothing to bill".
// With the R115 calendar hub the calendar's WO editor lives in the hub's
// inspector (calendar/maint); Supplies & Expenses keeps this lite view.
//
// WRITES: comments (POST …/comments) and the booked day (POST …/schedule) —
// nothing else. The thread is internal (accounting ↔ ground team), never an
// owner or vendor surface; admin-only ✕ remove stays on the Maintenance card.
// Every value goes through Formatters.escapeHtml. No AI call is ever awaited.
//
// R123 (Issues board; every new option defaults to today's behaviour, so the
// Calendar and Supplies & Expenses are unchanged):
//   zIndex     the viewer's stacking order (Issues passes 9700: its modals sit
//              at 9500 / 9600).
//   canEdit    office editors (the board passes isOffice(); D9 is client-side):
//              Who = a Person select (team ∪ inspectors ∪ cleaners) + a Vendor
//              select (by id), each with a clear option → POST /work-orders/:id
//              { assigned_to | vendor_id, surface:'issues', by } (the route
//              Slack-DMs a new assignee); Due = a date or "at next checkout" →
//              { due_date | due_source, surface:'issues', by }; the booked day
//              moves into the same Dispatch block (POST …/schedule, no native
//              confirm); "Open in Calendar" (openCalendar ops + wo_id); and
//              "Close WO…" only when onResolve is given. Saves send ONLY what
//              changed (lib/woEdit diffWoEdit, money never), keep the other
//              editor's unsaved draft across redraws, and re-read the record.
//   canBook    Book / Move the booked day from this viewer (default true, so the
//              Calendar and Supplies & Expenses keep it for every staff role).
//              The board passes isOffice(): D9 keeps booking office-only there,
//              so a field login sees the booked day but no Book / Move control.
//   focus      'who' | 'book' | 'due': the control that takes focus on open.
//   onResolve  "Close WO…" closes the viewer and hands the record to the host's
//              resolve flow (the board writes the undo stamps); the viewer
//              NEVER writes WO status itself.
//   people / vendors  preloaded rosters (else loaded once via woFields).
// R123 fixes: a typed Booked day is a draft like Who / Due (edBook), so a redraw
// (another save, a comment, the re-read) never resets it and Book / Move /
// Rebook books THAT day; the anchored popover remembers its anchor's last box,
// so a host redraw that detaches the anchor (the board's re-read) keeps the
// popover where it is instead of pinning it to the top-left corner.
// A host copy shaped like row.dispatch (lib/woCalendar woDispatch) renders
// instantly too (mapped to the WO fields, money hidden until the read lands).
import { Formatters } from '../lib/formatters';
import { icon } from './icons';
import {
  fmtDate, fmtDay, fmtDateTime, fmtMoney, fmtMins, todayLocal, woDoneAt, schedOf, schedEndOf, dueOf,
  statusLabel, priorityOf, priorityLabel, WO_BILLED_LABEL, WO_BILLED_TITLE, isPlaceholderDesc,
} from '../lib/woFormat';
import { mediaStrip, bindMediaStrips, isVideoUrl, type MediaItem } from '../lib/media';   // R116 #5: a before / after CLIP renders as a clip (it drew as a broken <img>)
import { diffWoEdit } from '../../../../lib/woEdit.mjs';
import { stripWoMoney, canSeeWoMoney } from '../../../../lib/woCalendar.mjs';
import { bizDay } from '../../../../lib/issueStage.mjs';
// R123: importable without the pages themselves (no App ↔ page cycle): the Calendar deep link and the dispatch rosters
import { openCalendar } from '../../pages/calendar/link';
import { loadDispatchRosters, personOptionsHtml, vendorOptionsHtml, type RosterPerson, type RosterVendor } from '../../pages/issues/woFields';

const API = '/api/assetops/work-orders';
const ROOT_ID = 'wo-qv';
const esc = (x: any): string => Formatters.escapeHtml(String(x == null ? '' : x));

export interface WoQuickViewOpts {
  /** The host's own copy of the record — renders instantly while the detail read lands. */
  wo?: any;
  /** Lets the list-route fallback stay narrow when the detail route is unavailable. */
  listingId?: string;
  /** Anchor element → popover placed next to it (wide screens only). */
  anchor?: HTMLElement;
  /** Fires after a comment posts or the booked day moves, with the fresh record. */
  onChange?: (wo: any) => void;
  /** Accounting partner: "Edit" hands the record back to the host's edit modal. */
  onEdit?: (wo: any) => void;
  // ── R123 (Issues board) — additive; every default keeps today's behaviour. ──
  /** Stacking order of the viewer. Default unchanged (9000); Issues passes 9700 to sit above its 9500/9600 modals. */
  zIndex?: number;
  /** Office editors: Who (Person + Vendor), Due (date / at next checkout), Open in Calendar, and Close WO… when onResolve is given. Default false. */
  canEdit?: boolean;
  /** Book / Move the booked day here. Default true (the Calendar and Supplies & Expenses unchanged); Issues passes isOffice() (D9: booking from the board is office-only). */
  canBook?: boolean;
  /** Which part to focus on open. */
  focus?: 'who' | 'book' | 'due';
  /** "Close WO…" closes the viewer and hands the record to the host's resolve flow (the viewer never writes WO status). */
  onResolve?: (wo: any) => void;
  /** Preloaded rosters for the Who editors (otherwise loaded on demand). */
  people?: { name: string; kind: string }[];
  vendors?: { id: string; name: string }[];
}

let current: WoQuickView | null = null;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** R123: a host copy shaped like row.dispatch (woDispatch) → the WO fields this viewer reads. Anything else passes through. */
function instantCopy(w: any): any {
  if (!w || typeof w !== 'object' || !('who_kind' in w) || !('booked' in w) || 'scheduled_date' in w || 'scheduled_window' in w) return w;
  const booked = typeof w.booked === 'string' && DAY.test(w.booked) ? w.booked : null;
  const end = typeof w.booked_end === 'string' && DAY.test(w.booked_end) && booked && w.booked_end > booked ? w.booked_end : null;
  return {
    id: w.id, title: w.title, status: w.status, priority: w.priority, role: w.role,
    assigned_to: w.assigned_to || '', vendor_id: w.vendor_id || null, vendor_name: w.vendor_name || '',
    scheduled_date: end ? null : booked, scheduled_window: booked ? { start: booked, end: end || booked } : null,
    due_date: w.due_source === 'checkout' ? null : (w.due || null), due_source: w.due_source || null, effective_due: w.due_source === 'checkout' ? (w.due || null) : null,
    timer_started_at: w.timer_on ? 'running' : null, owner_portal: w.owner_state ? { state: w.owner_state } : null,
    money_hidden: true,   // a dispatch never carries money; the detail read decides
  };
}
type EdKey = 'who' | 'due';

export class WoQuickView {
  static isOpen(): boolean { return !!(current && current.root.isConnected); }
  static open(id: string, opts: WoQuickViewOpts = {}): WoQuickView {
    if (current) current.close();
    const v = new WoQuickView(id, opts);
    current = v;
    v.mount();
    return v;
  }
  static close(): void { if (current) current.close(); }
  /** The house deep link: Maintenance opens focused on the WO (sessionStorage + app:navigate); a page already on Maintenance also hears 'mn:focus-wo'. */
  static focusInMaintenance(id: string, o: { textVendor?: boolean } = {}): void {
    try { sessionStorage.setItem('mn_focus_wo', id); if (o.textVendor) sessionStorage.setItem('mn_text_wo', id); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('mn:focus-wo', { detail: id }));
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'maintenance' }));
  }
  private static role(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  private static actorName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); }
    catch { return ''; }
  }

  private root: HTMLElement;
  private id: string;
  private opts: WoQuickViewOpts;
  private wo: any;
  private loading = true;
  private err = '';
  private busy = '';
  private draft = '';
  private anchored = false;
  // R123 editors (canEdit): unsaved drafts survive every redraw; undefined = untouched
  private edWho: { assigned_to?: string; vendor_id?: string } = {};
  private edDue: { due_date?: string; due_checkout?: boolean } = {};
  private edBook: string | undefined = undefined;   // the typed Booked day (both the Dispatch block and the old Book row); spent by a successful book
  private lastRect: { left: number; top: number; bottom: number } | null = null;   // the anchor's last live box (a detached anchor reads all zeros)
  private edMsg: Record<EdKey | 'book', { text: string; tone: '' | 'ok' | 'bad' }> = { who: { text: '', tone: '' }, due: { text: '', tone: '' }, book: { text: '', tone: '' } };
  private people: RosterPerson[] | null = null;
  private vendors: RosterVendor[] | null = null;
  private rosterFailed = false;
  private pendingFocus: 'who' | 'book' | 'due' | null = null;
  private ring: 'who' | 'book' | 'due' | null = null;   // the group the viewer was opened for (outlined while open)

  private constructor(id: string, opts: WoQuickViewOpts) {
    this.id = String(id);
    this.opts = opts;
    this.wo = opts.wo && String(opts.wo.id) === this.id ? instantCopy(opts.wo) : null;
    this.root = document.createElement('div');
    this.pendingFocus = opts.focus === 'who' || opts.focus === 'book' || opts.focus === 'due' ? opts.focus : null;
    if (Array.isArray(opts.people)) this.people = opts.people.filter(p => p && p.name).map(p => ({ name: String(p.name), kind: String(p.kind || '') }));
    if (Array.isArray(opts.vendors)) this.vendors = opts.vendors.filter(v => v && v.id != null).map(v => ({ id: String(v.id), name: String(v.name || '') }));
  }

  /** R123: the office editors are on (canEdit, a staff role that is not accounting / owner). */
  private editOn(): boolean {
    if (this.opts.canEdit !== true) return false;
    const role = WoQuickView.role();
    return role !== 'accounting' && role !== 'owner';
  }
  /** R123 (D9): the booked day can be booked / moved from here (default on; the Issues board turns it off for field logins). */
  private bookOn(): boolean { return this.opts.canBook !== false; }
  /** The value the day input shows: the unsaved draft, else the record's booked day. */
  private bookVal(sched: string | null): string { return this.edBook !== undefined ? this.edBook : (sched || ''); }
  /** R123 fix: the anchor's box while it is on the page; once a host redraw detaches it, the last box it had (null when it never had one). */
  private anchorRect(): { left: number; top: number; bottom: number } | null {
    const a = this.opts.anchor;
    if (a && a.isConnected) {
      const r = a.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) this.lastRect = { left: r.left, top: r.top, bottom: r.bottom };
    }
    return this.lastRect;
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab') return;
    const f = Array.from(this.root.querySelectorAll<HTMLElement>('button, [href], input, textarea, select')).filter(el => !el.hasAttribute('disabled'));
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  private mount(): void {
    WoQuickView.injectStyle();
    this.anchored = !!(this.opts.anchor && window.innerWidth >= 720 && this.anchorRect());   // an anchor with no box (detached / hidden) → the centered modal
    this.root.id = ROOT_ID;
    this.root.className = `wo-qv ${this.anchored ? 'wo-qv-anchored' : 'wo-qv-modal'}`;
    if (typeof this.opts.zIndex === 'number' && Number.isFinite(this.opts.zIndex)) this.root.style.zIndex = String(Math.round(this.opts.zIndex));   // R123
    document.body.appendChild(this.root);
    document.addEventListener('keydown', this.onKey);
    this.draw();
    void this.load();
    if (this.editOn() && (!this.people || !this.vendors)) void this.loadRosters();
  }

  /** R123: the Who rosters (preloaded through opts, else woFields' cached load). Patches the selects in place. */
  private async loadRosters(): Promise<void> {
    try {
      const r = await loadDispatchRosters();
      if (!this.people) this.people = r.people;
      if (!this.vendors) this.vendors = r.vendors;
      this.rosterFailed = !r.people.length && !r.vendors.length;
    } catch { this.rosterFailed = true; if (!this.people) this.people = []; if (!this.vendors) this.vendors = []; }
    if (!this.root.isConnected) return;
    const w = this.wo;
    const p = this.root.querySelector<HTMLSelectElement>('[data-qv-person]');
    const v = this.root.querySelector<HTMLSelectElement>('[data-qv-vendor]');
    if (w && p) { p.innerHTML = this.personOpts(w); p.disabled = this.busy === 'who'; }
    if (w && v) { v.innerHTML = this.vendorOpts(w); v.disabled = this.busy === 'who'; }
    const note = this.root.querySelector<HTMLElement>('[data-qv-roster-note]');
    if (note) note.textContent = this.rosterFailed ? "Couldn't load the team list. The current choice stays; reopen to try again." : '';
  }

  close(): void {
    this.root.remove();
    document.removeEventListener('keydown', this.onKey);
    if (current === this) current = null;
  }

  private async load(): Promise<void> {
    const r = await fetch(`${API}/${encodeURIComponent(this.id)}`).catch(() => null);
    if (r && r.ok) {
      const j = await r.json().catch(() => null);
      if (j && j.work_order) { this.wo = j.work_order; this.loading = false; this.err = ''; this.draw(); return; }
    }
    if (r && r.status === 404) { this.loading = false; if (!this.wo) this.err = 'Work order not found — it may have been deleted.'; this.draw(); return; }
    // Fallback: the list route (narrowed by listing when the host knows it).
    const q = this.opts.listingId ? `?listing_id=${encodeURIComponent(this.opts.listingId)}` : '';
    const l = await fetch(`${API}${q}`).then(x => x.ok ? x.json() : null).catch(() => null);
    const hit = l && Array.isArray(l.work_orders) ? l.work_orders.find((w: any) => String(w.id) === this.id) : null;
    this.loading = false;
    // R123: the list route is not money-stripped (the Maintenance board reads it); this viewer never shows a non-money role money
    const role = WoQuickView.role();
    if (hit) this.wo = role && !canSeeWoMoney(role) ? stripWoMoney(hit) : hit;
    else if (!this.wo) this.err = r && r.status === 403 ? 'Your role can\'t open work orders.' : 'Couldn\'t load this work order — check the server log.';
    this.draw();
  }

  private placement(): string {
    if (!this.anchored || !this.opts.anchor) return '';
    const rect = this.anchorRect();
    if (!rect) return '';
    const vw = window.innerWidth, vh = window.innerHeight;
    const PW = Math.min(400, vw - 16);
    const left = Math.max(8, Math.min(rect.left, vw - PW - 8));
    const maxH = Math.min(600, vh - 16);
    let top = rect.bottom + 6;
    if (top + Math.min(maxH, 360) > vh) top = Math.max(8, Math.min(rect.top - 6 - maxH, vh - maxH - 8));
    return `left:${Math.round(left)}px; top:${Math.round(top)}px; width:${PW}px; max-height:${Math.round(Math.min(maxH, vh - top - 8))}px;`;
  }

  private pill(label: string, tone = ''): string { return `<span class="wo-qv-pill ${tone}">${label}</span>`; }
  private row(k: string, v: string, cls = ''): string { return `<div class="wo-qv-row"><span class="wo-qv-k">${k}</span><span class="wo-qv-v ${cls}">${v}</span></div>`; }

  private threadHtml(w: any): string {
    const legacy: any[] = (Array.isArray(w.internal_notes) ? w.internal_notes : []).map((n: any) => ({ ...n, legacy: true }));
    const list: any[] = [...legacy, ...(Array.isArray(w.comments) ? w.comments : [])].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
    const role = WoQuickView.role();
    return `
      <div class="wo-qv-cmts">
        <div class="wo-qv-sec-h">💬 Internal comments${list.length ? ` · ${list.length}` : ''} <span class="wo-qv-muted" style="text-transform:none; letter-spacing:0; font-weight:400;">accounting ↔ ground team · never shown to owners or vendors</span></div>
        <div data-qv-thread>
          ${list.slice(-20).map((c: any) => `<div class="wo-qv-cmt"><div class="wo-qv-cmt-h"><b>${esc(c.by || '—')}</b>${c.legacy ? ' · note' : (c.role ? ` · ${esc(String(c.role).replace('_', ' '))}` : '')} · ${esc(String(c.at || '').slice(0, 16).replace('T', ' '))}</div><div class="wo-qv-cmt-t">${esc(c.text)}</div></div>`).join('') || '<div class="wo-qv-muted">No comments yet.</div>'}
        </div>
        ${role === 'owner' ? '' : `<div class="wo-qv-compose">
          <textarea data-qv-draft rows="1" placeholder="Comment for the team… (⌘/Ctrl+Enter posts)">${esc(this.draft)}</textarea>
          <button type="button" class="btn btn-primary" data-qv-post ${this.draft.trim() && this.busy !== 'post' ? '' : 'disabled'}>${this.busy === 'post' ? 'Posting…' : 'Post'}</button>
        </div>`}
      </div>`;
  }

  private draw(): void {
    const w = this.wo;
    const role = WoQuickView.role();
    const staff = !['accounting', 'owner', ''].includes(role) || role === '';   // '' = harness / unknown → treated as office staff
    const acct = role === 'accounting';
    const today = todayLocal();
    const office = !!w && staff && !acct && this.editOn();          // R123: canEdit (office) → Open in Calendar
    const edit = office && w.status !== 'done';                     // …and the Dispatch editors + Close WO… while the WO is open
    // R123: a redraw keeps focus on the same control (a roster / record landing mid-edit never steals it)
    const active = document.activeElement as HTMLElement | null;
    const keepAttr = this.editOn() && active && this.root.contains(active) ? Array.from(active.attributes).map(a => a.name).find(n => n.startsWith('data-qv-') && n !== 'data-qv-close') : undefined;
    let body = '';
    if (!w) {
      body = this.err ? `<div class="wo-qv-err">${esc(this.err)}</div>` : '<div class="wo-qv-muted" style="padding:18px 0; text-align:center;">Loading…</div>';
    } else {
      const prio = priorityOf(w);
      const prTone = prio === 'emergency' ? 'bad' : prio === 'urgent' ? 'warn' : prio === 'pm' ? 'info' : '';
      const stTone = w.status === 'done' ? 'ok' : w.status === 'in_progress' ? 'warn' : '';
      const doneAt = woDoneAt(w);
      const sched = schedOf(w), schedEnd = schedEndOf(w);
      const due = dueOf(w);
      const who = w.vendor_name || w.assigned_to || '';
      const op = w.owner_portal && w.owner_portal.state ? String(w.owner_portal.state) : '';
      const OP: Record<string, [string, string]> = { published: ['🏦 on portal', 'info'], needs_approval: ['🏦 awaiting owner', 'warn'], approved: ['🏦 owner approved', 'ok'], declined: ['🏦 owner declined', 'bad'] };
      const items: any[] = Array.isArray(w.items) ? w.items : [];
      const meta: any[] = Array.isArray(w.photos_meta) ? w.photos_meta : [];
      const videos: string[] = Array.isArray(w.videos) ? w.videos.filter((v: any) => typeof v === 'string') : [];
      const vidSet = new Set(videos);
      const asItem = (u: string): MediaItem => { const m = meta.find((x: any) => x && x.url === u); return { url: u, video: (m && m.media === 'video') || vidSet.has(u) || isVideoUrl(u) }; };
      const group = (label: string, urls: string[]) => urls.length ? `<div><div class="wo-qv-sec-h">${label}</div>${mediaStrip(urls.map(asItem), { size: 'sm', alt: 'WO photo' })}</div>` : '';
      const kindUrls = (k: string) => [...new Set(meta.filter((m: any) => m && m.kind === k && m.url).map((m: any) => String(m.url)))];
      const plain = [...new Set([...(Array.isArray(w.photos) ? w.photos : []), ...videos].filter((u: any) => typeof u === 'string' && !meta.some((m: any) => m && m.url === u && m.kind)).map(String))];
      const invoices: any[] = Array.isArray(w.invoices) ? w.invoices : [];
      const desc = isPlaceholderDesc(w.description) ? '' : String(w.description || '');
      const mins = Number(w.total_minutes) || 0;
      // the action row: at the bottom as always; with the R123 editors on it moves up under the pills (the Calendar inspector's head idiom)
      const actions = `
        <div class="wo-qv-actions${edit ? ' wo-qv-actions-top' : ''}">
          ${staff && !acct ? (edit
            ? `<button type="button" class="btn btn-primary" data-qv-open title="Open in Maintenance" aria-label="Open in Maintenance">${icon('wrench', 14)} Maintenance</button>`
            : `<button type="button" class="btn btn-primary" data-qv-open>${icon('wrench', 14)} Open in Maintenance</button>`) : ''}
          ${office ? `<button type="button" class="btn btn-secondary" data-qv-cal title="Open in Calendar: the full work-order panel on that day" aria-label="Open in Calendar">${icon('calendar', 14)} ${edit ? 'Calendar' : 'Open in Calendar'}</button>` : ''}
          ${edit && this.opts.onResolve ? '<button type="button" class="btn btn-secondary" data-qv-resolve title="Close the work order and resolve the issue">✓ Close WO…</button>' : ''}
          ${acct && this.opts.onEdit ? '<button type="button" class="btn btn-secondary" data-qv-edit>✎ Edit cost / payer / billing</button>' : ''}
          ${!edit && staff && !acct && w.status !== 'done' && this.bookOn() ? `<label class="wo-qv-book"><span class="wo-qv-muted">${sched ? 'Move day' : 'Book a day'}</span><input type="date" data-qv-date value="${esc(this.bookVal(sched))}" ${this.busy === 'book' ? 'disabled' : ''}><button type="button" class="btn btn-secondary" data-qv-book ${this.busy === 'book' ? 'disabled' : ''}>${this.busy === 'book' ? 'Saving…' : (sched ? 'Move' : 'Book')}</button></label>` : ''}
        </div>`;
      body = `
        <div class="wo-qv-pills">
          ${this.pill(w.type === 'pm' ? 'PM' : 'WO', 'acc')}${this.pill(esc(priorityLabel(prio)), prTone)}${this.pill(esc(statusLabel(w.status)), stTone)}
          ${op && OP[op] ? this.pill(OP[op][0], OP[op][1]) : ''}
          ${w.claim_tracked ? this.pill('🛡 claim tracked', 'info') : ''}
          ${w.source === 'supexp' ? this.pill('from QuickBooks line', '') : ''}
        </div>
        ${edit ? `${actions}${this.dispatchHtml(w, { who, sched, schedEnd, due, today })}` : ''}
        <div class="wo-qv-kv">
          ${edit ? '' : `${this.row('Who', who ? esc(who) : '<span class="wo-qv-muted">nobody assigned</span>')}
          ${this.row('Booked', sched
            ? `${esc(fmtDay(sched))}${schedEnd ? ` → ${esc(fmtDay(schedEnd))}` : ''}${sched < today && w.status !== 'done' ? ' <span class="wo-qv-muted">· was booked</span>' : ''}`
            : '<span class="wo-qv-muted">not booked</span>', sched && sched < today && w.status !== 'done' ? 'late' : '')}
          ${this.row('Due', due.source === 'checkout'
            ? `🔁 due at checkout${due.date ? ` (${esc(fmtDay(due.date))})` : ''}`
            : due.date ? esc(fmtDay(due.date)) : '<span class="wo-qv-muted">no due date</span>', due.date && due.date < today && w.status !== 'done' ? 'late' : '')}`}
          ${w.money_hidden ? '' : this.row('Cost', w.cost != null && Number(w.cost) !== 0 ? `${esc(fmtMoney(w.cost))}${w.payer ? ` <span class="wo-qv-muted">· ${w.payer === 'owner' ? 'owner pays' : 'WCH pays'}</span>` : ''}` : (w.payer ? `<span class="wo-qv-muted">no cost yet · ${w.payer === 'owner' ? 'owner pays' : 'WCH pays'}</span>` : '<span class="wo-qv-muted">—</span>'))}
          ${w.money_hidden ? '' : this.row('Billed', w.billing_status
            ? `<span class="wo-qv-pill ok" title="${esc((WO_BILLED_TITLE as any)[w.billing_status] || 'Owner has been charged')}">${esc((WO_BILLED_LABEL as any)[w.billing_status] || 'billed')}</span>`
            : w.payer === 'owner' ? '<span class="wo-qv-pill warn" title="Owner pays, but hasn\'t been charged yet">unbilled</span>' : '<span class="wo-qv-muted">nothing to bill the owner</span>')}
          ${w.guest_permission && w.guest_permission.granted ? this.row('Guest access', `🚪 ${w.guest_permission.mode === 'own_code' ? 'enter with own code' : 'guest will be present'}`) : ''}
          ${w.created_by ? this.row('Submitted by', esc(w.created_by)) : ''}
          ${this.row(doneAt ? 'Completed' : 'Opened', esc(doneAt ? fmtDateTime(doneAt) : fmtDate(w.created_at)))}
          ${mins || w.timer_started_at ? this.row('Labor', `${esc(fmtMins(mins))}${w.timer_started_at ? ' <span class="wo-qv-muted">· ⏱ running</span>' : ''}`) : ''}
        </div>
        ${desc ? `<div><div class="wo-qv-sec-h">Instructions</div><div class="wo-qv-text">${esc(desc)}</div></div>` : ''}
        ${w.completion && w.completion.what_done ? `<div><div class="wo-qv-sec-h">What was done</div><div class="wo-qv-text">${esc(w.completion.what_done)}</div>${w.completion.needs_followup ? `<div class="wo-qv-muted">Needs follow-up${w.completion.followup_note ? ` — ${esc(w.completion.followup_note)}` : ''}</div>` : ''}</div>` : (w.summary ? `<div><div class="wo-qv-sec-h">Owner-facing summary</div><div class="wo-qv-text">${esc(w.summary)}</div></div>` : '')}
        ${items.length ? `<div><div class="wo-qv-sec-h">Checklist · ${items.filter((i: any) => i && i.done).length}/${items.length} done</div><ul class="wo-qv-list">${items.slice(0, 20).map((i: any) => `<li>${i && i.done ? '☑' : '☐'} ${esc(i && i.title)}</li>`).join('')}</ul></div>` : ''}
        ${invoices.length ? `<div><div class="wo-qv-sec-h">Invoices &amp; receipts</div><div class="wo-qv-links">${invoices.map((inv: any) => `<a href="${esc(inv.url)}" target="_blank" rel="noopener" title="${esc(inv.name || 'invoice')}">🧾 ${esc(String(inv.name || 'invoice').slice(0, 28))}</a>`).join('')}</div></div>` : ''}
        ${group('📷 Before', kindUrls('before'))}${group('✅ After', kindUrls('after'))}${group('🧾 Receipts', kindUrls('receipt'))}${group(plain.some(u => asItem(u).video) ? 'Photos &amp; video' : 'Photos', plain)}
        ${this.threadHtml(w)}
        ${this.err ? `<div class="wo-qv-err">${esc(this.err)}</div>` : ''}
        ${edit ? '' : actions}
        <div class="wo-qv-fine">${acct ? 'Cost, who pays and billed-yet are edited from the Supplies &amp; Expenses tab — this view is the same record the Maintenance board shows.' : edit ? (this.bookOn() ? 'Who, the booked day, the due date and comments save here. Everything else is edited on the Maintenance board.' : 'Who, the due date and comments save here. Everything else is edited on the Maintenance board.') : (this.bookOn() ? 'Comments and the booked day save here; everything else is edited on the Maintenance board.' : 'Comments save here; everything else is edited on the Maintenance board.')}</div>`;
    }
    this.root.innerHTML = `
      <div class="wo-qv-backdrop" data-qv-close></div>
      <div class="wo-qv-card" role="dialog" aria-modal="true" aria-label="Work order" style="${this.placement()}">
        <div class="wo-qv-head">
          <div class="wo-qv-title">${w ? esc(w.title || 'Work order') : (this.loading ? 'Work order' : 'Work order')}<div class="wo-qv-sub">${w ? esc(w.listing_name || w.listing_id || '') : ''}${this.loading && w ? ' · refreshing…' : ''}</div></div>
          <button type="button" class="wo-qv-x" data-qv-close aria-label="Close">✕</button>
        </div>
        <div class="wo-qv-body">${body}</div>
      </div>`;
    this.bind();
    if (keepAttr) { const again = this.root.querySelector<HTMLElement>(`[${keepAttr}]`); if (again && !again.hasAttribute('disabled')) again.focus(); }
    // R123 focus: the asked-for control, once, as soon as it is on screen (a done WO has none → dropped)
    if (this.pendingFocus && w) {
      const sel = this.pendingFocus === 'who' ? '[data-qv-person]' : this.pendingFocus === 'due' ? '[data-qv-due]' : '[data-qv-date]';
      const el = this.root.querySelector<HTMLElement>(sel);
      if (el) {
        const grp = el.closest<HTMLElement>('[data-qv-ed]');
        if (grp) { grp.classList.add('wo-qv-ed-focus'); this.ring = this.pendingFocus; }
        el.focus();
        try { el.scrollIntoView({ block: 'nearest' }); } catch { /* old engines */ }
      }
      if (el || !this.loading) this.pendingFocus = null;
    }
    const x = this.root.querySelector<HTMLElement>('.wo-qv-x');
    if (x && !this.root.contains(document.activeElement)) x.focus();
  }

  // ── R123: the Dispatch block (Who / Booked / Due editors) ──────────────────
  private personOpts(w: any): string {
    const cur = this.edWho.assigned_to !== undefined ? this.edWho.assigned_to : String(w.assigned_to || '').trim();
    const none = `<option value="">${String(w.assigned_to || '').trim() ? 'Nobody (clear)' : 'Nobody'}</option>`;
    if (!this.people) return `${none}${cur ? `<option value="${esc(cur)}" selected>${esc(cur)}</option>` : ''}<option value="" disabled>Loading the team…</option>`;
    return none + personOptionsHtml(this.people, cur);
  }
  private vendorOpts(w: any): string {
    const cur = this.edWho.vendor_id !== undefined ? this.edWho.vendor_id : (w.vendor_id ? String(w.vendor_id) : '');
    const curName = cur && String(w.vendor_id || '') === cur ? String(w.vendor_name || '') : '';
    const none = `<option value="">${w.vendor_id ? 'No vendor (clear)' : 'No vendor'}</option>`;
    if (!this.vendors) return `${none}${cur ? `<option value="${esc(cur)}" selected>${esc(curName || 'Current vendor')}</option>` : ''}<option value="" disabled>Loading vendors…</option>`;
    return none + vendorOptionsHtml(this.vendors, cur, curName);
  }
  /** The POST body for one editor's unsaved draft (only changed keys, never money), or null. */
  private edBody(k: EdKey): Record<string, any> | null {
    const w = this.wo;
    if (!w) return null;
    if (k === 'who') {
      const f: any = {};
      if (this.edWho.assigned_to !== undefined) f.assigned_to = this.edWho.assigned_to;
      if (this.edWho.vendor_id !== undefined) f.vendor_id = this.edWho.vendor_id;
      return Object.keys(f).length ? diffWoEdit(w, f, { money: false }) : null;
    }
    const f: any = {};
    if (this.edDue.due_checkout !== undefined) f.due_checkout = this.edDue.due_checkout;
    const co = this.edDue.due_checkout !== undefined ? this.edDue.due_checkout : w.due_source === 'checkout';
    // a date typed under "at next checkout" is not sent (the checkout decides); clearing the date clears it
    if (this.edDue.due_date !== undefined && !co) f.due_date = this.edDue.due_date;
    return Object.keys(f).length ? diffWoEdit(w, f, { money: false }) : null;
  }
  private edMsgHtml(k: EdKey | 'book'): string {
    const m = this.edMsg[k];
    return `<span class="wo-qv-ed-msg${m.tone ? ` ${m.tone}` : ''}" data-qv-msg="${k}" role="status">${esc(m.text)}</span>`;
  }
  private saveBtn(k: EdKey, label: string): string {
    const dirty = !!this.edBody(k);
    const busy = this.busy === k;
    return `<button type="button" class="btn btn-secondary wo-qv-save${dirty ? ' on' : ''}" data-qv-save="${k}" ${dirty && !busy ? '' : 'disabled'}>${busy ? 'Saving…' : label}</button>`;
  }
  private dispatchHtml(w: any, o: { who: string; sched: string | null; schedEnd: string | null; due: { date: string | null; source: string | null }; today: string }): string {
    const whoBusy = this.busy === 'who', dueBusy = this.busy === 'due', bookBusy = this.busy === 'book';
    const late = !!o.sched && o.sched < o.today;
    const co = this.edDue.due_checkout !== undefined ? this.edDue.due_checkout : w.due_source === 'checkout';
    const dueVal = this.edDue.due_date !== undefined ? this.edDue.due_date : (w.due_date ? String(w.due_date).slice(0, 10) : '');
    const dueNow = o.due.source === 'checkout' ? `at next checkout${o.due.date ? `, ${esc(fmtDay(o.due.date))}` : ''}` : o.due.date ? esc(fmtDay(o.due.date)) : 'no due date';
    const dueLate = !!o.due.date && o.due.date < o.today;
    return `
      <div class="wo-qv-disp" data-qv-dispatch>
        <div class="wo-qv-sec-h">Dispatch</div>
        <div class="wo-qv-ed${this.ring === 'who' ? ' wo-qv-ed-focus' : ''}" data-qv-ed="who">
          <div class="wo-qv-ed-h"><span class="wo-qv-ed-k">Who</span><span class="wo-qv-ed-now${o.who ? '' : ' warn'}">${o.who ? esc(o.who) : 'Nobody assigned'}</span></div>
          <div class="wo-qv-ed-grid">
            <label class="wo-qv-ed-f"><span class="wo-qv-ed-l">Person</span><select data-qv-person aria-label="Person" ${whoBusy ? 'disabled' : ''}>${this.personOpts(w)}</select></label>
            <label class="wo-qv-ed-f"><span class="wo-qv-ed-l">Vendor</span><select data-qv-vendor aria-label="Vendor" ${whoBusy ? 'disabled' : ''}>${this.vendorOpts(w)}</select></label>
          </div>
          <div class="wo-qv-ed-foot">${this.edMsgHtml('who')}<span class="wo-qv-ed-msg bad" data-qv-roster-note>${this.rosterFailed ? "Couldn't load the team list. The current choice stays; reopen to try again." : ''}</span>${this.saveBtn('who', 'Save who')}</div>
        </div>
        <div class="wo-qv-ed${this.ring === 'book' ? ' wo-qv-ed-focus' : ''}" data-qv-ed="book">
          <div class="wo-qv-ed-h"><span class="wo-qv-ed-k">Booked</span><span class="wo-qv-ed-now${late ? ' late' : o.sched ? '' : ' muted'}">${o.sched ? `${late ? 'was booked ' : ''}${esc(fmtDay(o.sched))}${o.schedEnd ? ` → ${esc(fmtDay(o.schedEnd))}` : ''}` : 'not booked'}</span></div>
          ${this.bookOn() ? `<div class="wo-qv-ed-row">
            <input type="date" data-qv-date aria-label="${o.sched ? 'Move to day' : 'Book a day'}" value="${esc(this.bookVal(o.sched))}" ${bookBusy ? 'disabled' : ''}>
            <button type="button" class="btn btn-secondary" data-qv-book ${bookBusy ? 'disabled' : ''}>${bookBusy ? 'Saving…' : (o.sched ? (late ? 'Rebook' : 'Move') : 'Book')}</button>
          </div>` : ''}
          ${this.edMsg.book.text ? `<div class="wo-qv-ed-foot">${this.edMsgHtml('book')}</div>` : '<span data-qv-msg="book" hidden></span>'}
        </div>
        <div class="wo-qv-ed${this.ring === 'due' ? ' wo-qv-ed-focus' : ''}" data-qv-ed="due">
          <div class="wo-qv-ed-h"><span class="wo-qv-ed-k">Due</span><span class="wo-qv-ed-now${dueLate ? ' late' : o.due.date || o.due.source ? '' : ' muted'}">${dueNow}</span></div>
          <div class="wo-qv-ed-row">
            <input type="date" data-qv-due aria-label="Due date" value="${esc(co ? '' : dueVal)}" ${co || dueBusy ? 'disabled' : ''}>
            <label class="wo-qv-chk"><input type="checkbox" data-qv-due-co ${co ? 'checked' : ''} ${dueBusy ? 'disabled' : ''}> at next checkout</label>
          </div>
          <div class="wo-qv-ed-foot">${this.edMsgHtml('due')}${this.saveBtn('due', 'Save due')}</div>
        </div>
      </div>`;
  }
  /** Refresh one editor's Save button + message without a redraw (a select change must not rebuild the card). */
  private patchEd(k: EdKey): void {
    const b = this.root.querySelector<HTMLButtonElement>(`[data-qv-save="${k}"]`);
    if (b) { const dirty = !!this.edBody(k); b.disabled = !dirty || this.busy === k; b.classList.toggle('on', dirty); }
    const m = this.root.querySelector<HTMLElement>(`[data-qv-msg="${k}"]`);
    if (m) { m.textContent = this.edMsg[k].text; m.className = `wo-qv-ed-msg${this.edMsg[k].tone ? ` ${this.edMsg[k].tone}` : ''}`; }
  }
  private async saveEd(k: EdKey): Promise<void> {
    const body = this.edBody(k);
    if (!body || !this.wo || this.busy) return;
    this.busy = k; this.edMsg[k] = { text: '', tone: '' }; this.draw();
    const r = await fetch(`${API}/${encodeURIComponent(this.id)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, surface: 'issues', by: WoQuickView.actorName() }) }).catch(() => null);
    const j = r ? await r.json().catch(() => null) : null;
    this.busy = '';
    if (!this.root.isConnected) return;
    if (!r || !r.ok) {
      const why = !r ? 'no connection' : r.status === 403 ? "your role can't change work orders" : r.status === 404 ? 'the work order was deleted' : r.status === 503 ? "the work orders didn't load just now, try again" : String((j && (j.message || j.error)) || `HTTP ${r.status}`);
      this.edMsg[k] = { text: `Not saved: ${why.replace(/[.\s]+$/, '')}.`, tone: 'bad' };
      this.draw(); return;
    }
    let fresh: any = j && j.work_order && typeof j.work_order === 'object' ? j.work_order : {};
    if (this.wo.money_hidden) fresh = stripWoMoney(fresh);   // never widen what this role was shown
    const next: any = { ...this.wo, ...fresh };
    for (const key of Object.keys(body)) if (!(key in fresh)) next[key] = body[key];   // the answer always wins; the sent value fills a gap
    if ('vendor_id' in body) next.vendor_name = body.vendor_id ? ((this.vendors || []).find(v => v.id === String(body.vendor_id)) || { name: '' }).name : '';
    if (this.wo.listing_name && !next.listing_name) next.listing_name = this.wo.listing_name;
    this.wo = next;
    if (k === 'who') this.edWho = {}; else this.edDue = {};
    this.edMsg[k] = { text: 'Saved.', tone: 'ok' };
    this.draw();
    if (this.opts.onChange) { try { this.opts.onChange(this.wo); } catch { /* host's problem */ } }
    void this.load();   // re-read: the effective due (next checkout) and the vendor name come from the detail route
  }
  private openInCalendar(): void {
    const w = this.wo || {};
    const date = schedOf(w) || bizDay();
    const base = { view: 'ops', date, wo_id: this.id, src: 'issues' };
    const lid = w.listing_id != null ? String(w.listing_id) : (this.opts.listingId || '');
    this.close();
    if (!openCalendar(lid ? { ...base, listing_id: lid } : base)) openCalendar(base);
  }

  private bind(): void {
    bindMediaStrips(this.root);   // R116 #5: tap → the shared viewer (photos + clips)
    this.root.querySelectorAll<HTMLElement>('[data-qv-close]').forEach(el => el.addEventListener('click', () => this.close()));
    this.root.querySelector('[data-qv-open]')?.addEventListener('click', () => { const id = this.id; this.close(); WoQuickView.focusInMaintenance(id); });
    this.root.querySelector('[data-qv-edit]')?.addEventListener('click', () => { const w = this.wo; this.close(); if (this.opts.onEdit && w) this.opts.onEdit(w); });
    const ta = this.root.querySelector<HTMLTextAreaElement>('[data-qv-draft]');
    const post = this.root.querySelector<HTMLButtonElement>('[data-qv-post]');
    ta?.addEventListener('input', () => { this.draft = ta.value; if (post) post.disabled = !ta.value.trim(); });
    ta?.addEventListener('keydown', (e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post?.click(); } });
    post?.addEventListener('click', () => void this.postComment());
    this.root.querySelector('[data-qv-book]')?.addEventListener('click', () => {
      const inp = this.root.querySelector<HTMLInputElement>('[data-qv-date]');
      void this.book(inp ? inp.value : '');
    });
    // R123 editors
    this.root.querySelector('[data-qv-cal]')?.addEventListener('click', () => this.openInCalendar());
    this.root.querySelector('[data-qv-resolve]')?.addEventListener('click', () => { const w = this.wo; const hook = this.opts.onResolve; this.close(); if (hook && w) { try { hook(w); } catch { /* host's problem */ } } });
    const person = this.root.querySelector<HTMLSelectElement>('[data-qv-person]');
    const vendor = this.root.querySelector<HTMLSelectElement>('[data-qv-vendor]');
    person?.addEventListener('change', () => { this.edWho.assigned_to = person.value; this.edMsg.who = { text: '', tone: '' }; this.patchEd('who'); });
    vendor?.addEventListener('change', () => { this.edWho.vendor_id = vendor.value; this.edMsg.who = { text: '', tone: '' }; this.patchEd('who'); });
    const due = this.root.querySelector<HTMLInputElement>('[data-qv-due]');
    const co = this.root.querySelector<HTMLInputElement>('[data-qv-due-co]');
    const onDue = () => { if (due) { this.edDue.due_date = due.value; this.edMsg.due = { text: '', tone: '' }; this.patchEd('due'); } };
    due?.addEventListener('input', onDue); due?.addEventListener('change', onDue);
    co?.addEventListener('change', () => {
      this.edDue.due_checkout = co.checked;
      if (due) {
        due.disabled = co.checked;
        due.value = co.checked ? '' : (this.edDue.due_date !== undefined ? this.edDue.due_date : (this.wo && this.wo.due_date ? String(this.wo.due_date).slice(0, 10) : ''));
      }
      this.edMsg.due = { text: '', tone: '' };
      this.patchEd('due');
    });
    this.root.querySelectorAll<HTMLElement>('[data-qv-save]').forEach(b => b.addEventListener('click', () => void this.saveEd(b.dataset.qvSave === 'due' ? 'due' : 'who')));
    // R123 fix: the typed day is a draft (a redraw keeps it; Book / Move books it), in the Dispatch block and the old Book row alike
    const day = this.root.querySelector<HTMLInputElement>('[data-qv-date]');
    const onDay = () => {
      if (!day) return;
      this.edBook = day.value;
      if (this.edMsg.book.text) { this.edMsg.book = { text: '', tone: '' }; const m = this.root.querySelector<HTMLElement>('[data-qv-msg="book"]'); if (m) m.textContent = ''; }
    };
    day?.addEventListener('input', onDay); day?.addEventListener('change', onDay);
  }

  private async postComment(): Promise<void> {
    const text = this.draft.trim();
    if (!text || !this.wo) return;
    this.busy = 'post'; this.draw();
    const r = await fetch(`${API}/${encodeURIComponent(this.id)}/comments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, by: WoQuickView.actorName() }) }).catch(() => null);
    const j = r ? await r.json().catch(() => null) : null;
    this.busy = '';
    if (!r || !r.ok || !j) { this.err = (j && j.error) || 'Comment didn\'t save — try again.'; this.draw(); return; }
    this.err = ''; this.draft = '';
    this.wo.comments = Array.isArray(j.comments) ? j.comments : this.wo.comments;
    // patch ONLY the thread (the rest of the card has nothing unsaved, but the
    // host's own state must learn the new count)
    const host = this.root.querySelector<HTMLElement>('[data-qv-thread]');
    if (host) { const tmp = document.createElement('div'); tmp.innerHTML = this.threadHtml(this.wo); const fresh = tmp.querySelector('[data-qv-thread]'); if (fresh) host.innerHTML = fresh.innerHTML; }
    const ta = this.root.querySelector<HTMLTextAreaElement>('[data-qv-draft]'); if (ta) ta.value = '';
    const post = this.root.querySelector<HTMLButtonElement>('[data-qv-post]'); if (post) { post.disabled = true; post.textContent = 'Post'; }
    if (this.opts.onChange) { try { this.opts.onChange(this.wo); } catch { /* host's problem */ } }
  }

  private async book(date: string): Promise<void> {
    if (!this.wo || !this.bookOn()) return;   // D9: never from a viewer that hides Book / Move
    // R123: with the office editors on, the Dispatch block answers inline (no native confirm; the Book / Move click is the decision)
    const ed = this.editOn();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      if (ed) { this.edMsg.book = { text: 'Pick a day first.', tone: 'bad' }; this.draw(); return; }
      this.err = 'Pick a day first.'; this.draw(); return;
    }
    const moving = !!schedOf(this.wo);
    if (!ed && !window.confirm(`${moving ? 'Move' : 'Book'} "${String(this.wo.title || '')}" ${moving ? 'to' : 'for'} ${fmtDate(date)}?`)) return;
    this.busy = 'book'; this.err = ''; if (ed) this.edMsg.book = { text: '', tone: '' }; this.draw();
    const r = await fetch(`${API}/${encodeURIComponent(this.id)}/schedule`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ed ? { date, by: WoQuickView.actorName(), note: 'booked from the Issues board' } : { date, by: WoQuickView.actorName() }) }).catch(() => null);
    const j = r ? await r.json().catch(() => null) : null;
    this.busy = '';
    if (!r || !r.ok) {
      const msg = r && r.status === 409 ? 'This work order is already done — it can\'t be moved.' : r && r.status === 403 ? 'Your role can\'t reschedule work orders.' : `Couldn't move it — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`;
      if (ed) this.edMsg.book = { text: r && r.status === 409 ? "This work order is already done, so it can't be moved." : r && r.status === 403 ? "Your role can't reschedule work orders." : `Not booked: ${String((j && (j.message || j.error)) || (r ? `HTTP ${r.status}` : 'no connection')).replace(/[.\s]+$/, '')}.`, tone: 'bad' };
      else this.err = msg;
      this.draw(); return;
    }
    // R123: the answer never widens what this role was shown (the schedule route's record may carry money)
    if (j && j.work_order) this.wo = { ...this.wo, ...(this.wo.money_hidden ? stripWoMoney(j.work_order) : j.work_order) };
    else { this.wo.scheduled_date = date; this.wo.scheduled_window = { start: date, end: date }; }
    this.edBook = undefined;   // booked: the draft is spent, the record's day shows from here on
    if (ed) this.edMsg.book = { text: `${moving ? 'Moved to' : 'Booked for'} ${fmtDay(date)}.`, tone: 'ok' };
    this.draw();
    if (this.opts.onChange) { try { this.opts.onChange(this.wo); } catch { /* host's problem */ } }
  }

  private static injectStyle(): void {
    if (document.getElementById('wo-qv-style')) return;
    const s = document.createElement('style');
    s.id = 'wo-qv-style';
    s.textContent = `
      .wo-qv { position:fixed; inset:0; z-index:9000; }
      .wo-qv-modal { display:flex; align-items:flex-end; justify-content:center; }
      @media (min-width:640px) { .wo-qv-modal { align-items:center; padding:16px; } }
      .wo-qv-backdrop { position:absolute; inset:0; background:rgba(15,23,42,.5); }
      .wo-qv-anchored .wo-qv-backdrop { background:transparent; }
      .wo-qv-card { position:relative; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg) var(--radius-lg) 0 0; box-shadow:var(--shadow-pop); width:100%; max-width:560px; max-height:92vh; overflow:auto; display:flex; flex-direction:column; box-sizing:border-box; }
      @media (min-width:640px) { .wo-qv-modal .wo-qv-card { border-radius:var(--radius-lg); } }
      .wo-qv-anchored .wo-qv-card { position:fixed; border-radius:var(--radius); max-width:none; }
      .wo-qv-head { display:flex; gap:10px; align-items:flex-start; padding:14px 16px 10px; border-bottom:1px solid var(--border-soft); position:sticky; top:0; background:var(--card); z-index:1; }
      .wo-qv-title { flex:1; min-width:0; font-size:14px; font-weight:700; color:var(--text); line-height:1.35; overflow-wrap:anywhere; }
      .wo-qv-sub { font-size:12px; color:var(--text-faint); font-weight:400; margin-top:2px; }
      .wo-qv-x { flex:none; width:30px; height:30px; border-radius:var(--radius-pill); border:none; background:transparent; color:var(--text-muted); font-size:15px; cursor:pointer; }
      .wo-qv-x:hover { background:var(--bg-subtle); color:var(--text); }
      .wo-qv-x:focus-visible { outline:none; box-shadow:var(--focus-ring); }
      .wo-qv-body { padding:10px 16px 14px; display:flex; flex-direction:column; gap:10px; }
      .wo-qv-pills { display:flex; gap:6px; flex-wrap:wrap; }
      .wo-qv-pill { display:inline-flex; align-items:center; gap:4px; font-size:10.5px; font-weight:600; padding:2px 9px; border-radius:var(--radius-pill); white-space:nowrap; background:var(--bg-subtle); color:var(--text-muted); line-height:1.5; }
      .wo-qv-pill.ok { background:var(--success-soft); color:var(--success-text); }
      .wo-qv-pill.warn { background:var(--warning-soft); color:var(--warning-text); }
      .wo-qv-pill.bad { background:var(--danger-soft); color:var(--danger-text); }
      .wo-qv-pill.info { background:var(--info-soft); color:var(--info-text); }
      .wo-qv-pill.acc { background:var(--accent-soft); color:var(--accent); border:1px solid var(--accent-border); }
      .wo-qv-kv { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); padding:2px 12px; }
      .wo-qv-row { display:flex; justify-content:space-between; gap:12px; padding:7px 0; border-bottom:1px solid var(--border-soft); font-size:12.5px; }
      .wo-qv-row:last-child { border-bottom:none; }
      .wo-qv-k { color:var(--text-muted); flex:none; }
      .wo-qv-v { color:var(--text); font-weight:500; text-align:right; min-width:0; overflow-wrap:anywhere; }
      .wo-qv-v.late { color:var(--danger-text); }
      .wo-qv-sec-h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin-bottom:4px; }
      .wo-qv-text { font-size:12.5px; white-space:pre-wrap; line-height:1.5; color:var(--text); }
      .wo-qv-muted { color:var(--text-faint); font-size:12px; font-weight:400; }
      .wo-qv-list { margin:0; padding-left:4px; list-style:none; font-size:12.5px; line-height:1.6; }
      .wo-qv-links { display:flex; gap:10px; flex-wrap:wrap; font-size:12px; }
      .wo-qv-links a { color:var(--accent); text-decoration:none; font-weight:600; }
      .wo-qv-photos { display:flex; gap:6px; flex-wrap:wrap; }
      .wo-qv-photos img { width:64px; height:64px; object-fit:cover; border-radius:var(--radius-sm); border:1px solid var(--border); display:block; }
      .wo-qv-cmts { border:1px solid var(--border); border-radius:var(--radius); padding:8px 10px; background:var(--bg-subtle); }
      .wo-qv-cmt { border-left:2px solid var(--border); padding:2px 8px; margin:4px 0; }
      .wo-qv-cmt-h { font-size:11px; color:var(--text-muted); }
      .wo-qv-cmt-t { font-size:12px; white-space:pre-wrap; line-height:1.45; color:var(--text); }
      .wo-qv-compose { display:flex; gap:6px; align-items:flex-start; margin-top:6px; }
      .wo-qv-compose textarea { flex:1; min-height:32px; padding:6px 9px; border:1px solid var(--border); border-radius:var(--radius-sm); font:inherit; font-size:12px; resize:vertical; background:var(--card); color:var(--text); }
      .wo-qv-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
      .wo-qv-actions-top { padding-bottom:2px; }
      .wo-qv-book { display:flex; gap:6px; align-items:center; margin-left:auto; flex-wrap:wrap; }
      .wo-qv-book input { padding:6px 8px; border:1px solid var(--border); border-radius:var(--radius-sm); font:inherit; font-size:12px; background:var(--card); color:var(--text); }
      .wo-qv-err { padding:8px 10px; border-radius:var(--radius-sm); background:var(--danger-soft); color:var(--danger-text); font-size:12px; line-height:1.45; }
      .wo-qv-fine { font-size:11px; color:var(--text-faint); line-height:1.45; }
      .wo-qv .btn { padding:7px 12px; font-size:12px; }
      /* R123: the office Dispatch block (Who / Booked / Due editors); the Calendar inspector's form idiom, tokens only */
      .wo-qv-disp { display:flex; flex-direction:column; gap:8px; }
      .wo-qv-disp > .wo-qv-sec-h { margin-bottom:0; }
      .wo-qv-ed { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); padding:8px 12px 10px; display:flex; flex-direction:column; gap:7px; min-width:0; }
      .wo-qv-ed.wo-qv-ed-focus { border-color:var(--accent-border); box-shadow:var(--focus-ring); }
      .wo-qv-ed-h { display:flex; justify-content:space-between; align-items:baseline; gap:12px; font-size:12.5px; }
      .wo-qv-ed-k { color:var(--text-muted); flex:none; }
      .wo-qv-ed-now { color:var(--text); font-weight:600; text-align:right; min-width:0; overflow-wrap:anywhere; }
      .wo-qv-ed-now.late { color:var(--danger-text); }
      .wo-qv-ed-now.warn { color:var(--warning-text); }
      .wo-qv-ed-now.muted { color:var(--text-faint); font-weight:400; }
      .wo-qv-ed-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; }
      .wo-qv-ed-f { display:flex; flex-direction:column; gap:3px; min-width:0; }
      .wo-qv-ed-l { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); }
      .wo-qv-ed select, .wo-qv-ed input[type="date"] { box-sizing:border-box; width:100%; min-width:0; max-width:100%; min-height:34px; padding:6px 9px; border:1px solid var(--border); border-radius:var(--radius-sm); font:inherit; font-size:12.5px; background:var(--card); color:var(--text); }
      .wo-qv-ed select:focus, .wo-qv-ed input[type="date"]:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
      .wo-qv-ed input[type="date"]:disabled { background:var(--bg-subtle); color:var(--text-faint); }
      .wo-qv-ed-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; min-width:0; }
      .wo-qv-ed-row input[type="date"] { flex:1 1 150px; width:auto; }
      .wo-qv-chk { display:inline-flex; align-items:center; gap:6px; min-height:34px; font-size:12.5px; color:var(--text); cursor:pointer; white-space:nowrap; }
      .wo-qv-chk input { width:16px; height:16px; margin:0; accent-color:var(--accent); }
      .wo-qv-ed-foot { display:flex; align-items:center; justify-content:flex-end; gap:8px; flex-wrap:wrap; }
      .wo-qv-ed-msg { flex:1 1 auto; min-width:0; font-size:11.5px; color:var(--text-muted); line-height:1.4; }
      .wo-qv-ed-msg:empty { display:none; }
      .wo-qv-ed-msg.ok { color:var(--success-text); font-weight:600; }
      .wo-qv-ed-msg.bad { color:var(--danger-text); font-weight:600; }
      .wo-qv-save.on { border-color:var(--accent); color:var(--accent-hover); box-shadow:inset 0 0 0 1px var(--accent); }
      @media (max-width:640px) {
        .wo-qv-x { width:36px; height:36px; }
        .wo-qv-ed-grid { grid-template-columns:minmax(0, 1fr); }
        .wo-qv-ed select, .wo-qv-ed input[type="date"] { min-height:40px; font-size:16px; }
        .wo-qv-chk { min-height:40px; }
      }
    `;
    document.head.appendChild(s);
  }
}

export function openWoQuickView(id: string, opts: WoQuickViewOpts = {}): WoQuickView { return WoQuickView.open(id, opts); }
export function closeWoQuickView(): void { WoQuickView.close(); }
